import { useDraggable } from "@dnd-kit/core";
import { AlertTriangle } from "lucide-react";
import type { BoardCard } from "@salesboard/contracts";
import { useT, type Translator } from "@/i18n";
import { Tooltip } from "@/components/ui/tooltip";
import { OwnerBadge } from "@/components/ui/OwnerBadge";
import { cn } from "@/lib/utils";
import { formatCurrencyCompact } from "@/lib/format";
import { useDragClick } from "./drag/useDragClick";

export type OpportunityCardProps = {
  card: BoardCard;
  /** False when the viewer may not move it — see the prevented-not-refused decision. */
  movable: boolean;
  /** Rendered at its target while a commit is in flight or a dialog is open. */
  pending?: boolean;
  onOpen: (card: BoardCard) => void;
  onBlockedDrag?: (card: BoardCard) => void;
  /**
   * Fired as the pointer arrives and on focus, so the detail's data is usually in the cache by
   * the time the click lands and the modal opens on its finished state rather than a skeleton.
   * A hint, not a requirement — see `usePrefetchDetail`.
   */
  onHover?: (card: BoardCard) => void;
};

/**
 * The card the board is made of.
 *
 * **Five facts** — client, opportunity name, expected revenue, stalled state, and the
 * owner, who is carried **twice**: as a soft tint of the surface and as an initials circle
 * on the amount row (the shared `OwnerBadge`, `components/ui/OwnerBadge.tsx` — extracted
 * from this file once the opportunities list gained the same badge, per
 * specs/pipeline-polish.md; two copies of one circle is how the card and the list would
 * start disagreeing about a colour). The owner used to be deliberately absent (the
 * client's own decision, DD 008); the developer's instruction reversed that, and the
 * amendment is recorded as M44 pending client ratification. [card-density](../../../../../specs/board/card-density.md)
 * folded the owner's name — a whole text row — into that circle, so the card holds three
 * rows instead of four; the full name moved from an always-visible line into the shared
 * `Tooltip` and stayed in `accessibleLabel`, which is unchanged by that move.
 *
 * The tint was bounded as a **glance aid, not the accessible carrier of ownership**; the
 * tooltip and the accessible label carry it now, alongside the detail modal, the Owner
 * column and the filter. The colour-never-alone rule governs *status*, and every status
 * keeps its non-colour cue — which is why won and lost surrender the background wash they
 * used to have but keep their border and their word. Initials are text too: `OwnerBadge`
 * picks black or white for whichever clears more contrast against the fill, verified for
 * every palette entry in `manager-colors.test.ts`.
 */
export function OpportunityCard({ card, movable, pending, onOpen, onBlockedDrag, onHover }: OpportunityCardProps) {
  const t = useT();
  const draggable = useDraggable({ id: `card:${card.id}`, data: { card }, disabled: !movable || pending });

  /**
   * dnd-kit's attributes, minus its keyboard-drag advertising.
   *
   * `aria-roledescription="draggable"` and the `aria-describedby` instructions ("To pick up
   * a draggable item, press the space bar…") are only true if a keyboard activator is
   * attached — and it is not: `useDragClick` forwards `onPointerDown` alone, so Space does
   * nothing here. Announcing an interaction that dead-ends is worse than announcing none,
   * and the real drag-free path is opening the card — click or Enter, from the keyboard as
   * from the pointer — and editing its stage or date inline in the detail modal.
   */
  const { role, tabIndex, "aria-disabled": ariaDisabled } = draggable.attributes;

  // dnd-kit's PointerSensor swallows the synthetic click, so a plain onClick on a
  // draggable is dead. See useDragClick — including why it must not stopPropagation.
  const click = useDragClick({
    enabled: true,
    isDragging: draggable.isDragging,
    onClick: () => onOpen(card),
    onBlocked: movable ? undefined : () => onBlockedDrag?.(card),
    dragListeners: draggable.listeners,
  });

  return (
    <article
      ref={draggable.setNodeRef}
      role={role}
      aria-disabled={ariaDisabled}
      {...click.handlers}
      onPointerEnter={() => onHover?.(card)}
      onFocus={() => onHover?.(card)}
      tabIndex={tabIndex ?? 0}
      aria-busy={pending}
      aria-label={accessibleLabel(t, card, movable)}
      className={cn(
        "border-border bg-card focus-visible:ring-ring rounded-md border p-2 text-left shadow-sm transition-shadow focus-visible:ring-2 focus-visible:outline-none",
        // Cards pan the board under a finger rather than fighting it for the gesture.
        "touch-pan-y",
        movable && !pending ? "cursor-grab hover:shadow-md active:cursor-grabbing" : "cursor-pointer",
        draggable.isDragging && "opacity-40",
        pending && "opacity-60",
        // Colour is never the sole carrier: won and lost also show a word, stalled a shape.
        //
        // Their *background* washes are gone, because one surface cannot hold two tints and
        // the owner's is the one the board is now read by. What carried the status was
        // always the border and the pill; those stay exactly as they were.
        card.status === "won" && "border-success/50",
        card.status === "lost" && "border-muted-foreground/30",
        // The status wash survives where there is no owner tint to replace it. One surface
        // cannot hold two, but the launch state is *everybody* unassigned — dropping it
        // unconditionally would have repainted every closed card on the board on day one,
        // in exchange for a tint that is not there.
        !card.ownerColor && card.status === "won" && "bg-success/5",
        !card.ownerColor && card.status === "lost" && "bg-muted/40",
        card.stalled && "border-l-destructive border-l-4",
        // Falls back to the plain card surface: nobody is assigned a colour automatically,
        // so an unassigned owner's cards must look exactly as they did before.
        card.ownerColor && "owner-tint",
      )}
      style={card.ownerColor ? ({ "--owner-color": `var(--manager-${card.ownerColor})` } as React.CSSProperties) : undefined}
    >
      <div className="flex items-start justify-between gap-1.5">
        <p className="board-type-primary min-w-0 truncate font-medium">{card.clientName}</p>
        {pending && (
          <span
            aria-hidden
            className="border-muted-foreground/40 border-t-primary mt-0.5 h-3 w-3 shrink-0 animate-spin rounded-full border-2"
          />
        )}
      </div>

      <p className="text-muted-foreground board-type-secondary mt-0.5 line-clamp-2">{card.name}</p>

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <OwnerBadge name={card.ownerName} color={card.ownerColor} />
          <span className="tabular board-type-secondary font-semibold">{formatCurrencyCompact(card.nip)}</span>
        </span>

        <span className="flex items-center gap-1">
          {card.status !== "open" && (
            <span
              className={cn(
                "board-type-meta rounded px-1 py-0.5 font-medium uppercase",
                card.status === "won" ? "bg-success/15 text-success" : "bg-muted text-muted-foreground",
              )}
            >
              {t(`status.${card.status}`)}
            </span>
          )}
          {card.stalled && (
            // The shared tooltip, never a native `title`: on a meeting-room display the
            // browser's own hint waits a second and then renders 11px grey. The full fact is
            // in the card's accessible name as well, so nothing here is hover-only.
            <Tooltip content={t("card.stalledTooltip", t("count.daysInStage", card.stageAgeDays))}>
              <span className="text-destructive board-type-meta inline-flex items-center gap-0.5 font-medium">
                {/* A shape as well as a colour, and the same fact in text for a reader. */}
                <AlertTriangle className="h-3 w-3" aria-hidden />
                <span className="sr-only">{t("card.stalledPrefix")}</span>
                {t("count.daysShort", card.stageAgeDays)}
              </span>
            </Tooltip>
          )}
        </span>
      </div>
    </article>
  );
}

/**
 * Every fact the surface holds, as one string.
 *
 * Exported since 2026-08-27: the condensed chip shows only the client's name, so this *is* the
 * rest of the record for a screen-reader user, and for the tooltip a sighted one gets. Two
 * copies of it is how the card and the chip would start disagreeing about what an opportunity
 * is.
 */
export function accessibleLabel(t: Translator, card: BoardCard, movable: boolean): string {
  const parts = [
    card.clientName,
    card.name,
    formatCurrencyCompact(card.nip),
    card.status === "won" ? t("card.closedWon") : card.status === "lost" ? t("card.closedLost") : undefined,
    card.stalled ? t("card.stalledAria", t("count.daysInStage", card.stageAgeDays)) : undefined,
    t("card.ownedBy", card.ownerName),
    movable ? undefined : t("card.notMovable"),
  ];
  return parts.filter(Boolean).join(", ");
}
